import dlv from 'dlv';
import { FieldValues } from 'react-hook-form';
import {
	EndorsementExposureUpdateRequest,
	EndorsementResponse,
	EndorsementUpdateRequest,
} from '../../../types/endorsements';
import {
	ExposureCreateRequest,
	ExposureUpdateRequest,
	PerilCreateRequest,
	PerilUpdateRequest,
} from '../../../types/policy';
import {
	RenewalExposureUpdateRequest,
	RenewalResponse,
	RenewalUpdateRequest,
} from '../../../types/renewals';
import {
	mergeCurrentAndUpdateObjects,
	mergeCurrentExposureAddObjectAndExposureAddObject,
	mergeCurrentExposureUpdateObjectAndExposureUpdateObject,
	mergeCurrentPerilUpdateObjectAndPerilUpdateObject,
	mergePerilCreateObjects,
} from '../../policy/util_sharedEndorsementAndRenewal';
import { FormBuilder, FormShape, SaveOptions } from '../types';
import {
	createPerilRequestBuilder,
	filterRemovedFromUpdated,
	getPathsForForms,
	updatePerilRequestBuilder,
	updateRequestBuilder,
} from '../utils';

const endorsementRenewalUpdateBuilder = (
	dirtyFields: { [key: string]: boolean },
	formValues: FieldValues,
	fields: FormShape<FormBuilder>,
	primaryFormName: string,
	{
		addExposure = undefined,
		removeExposure = undefined,
		addPeril = undefined,
		removePeril = undefined,
		removeAddedExposure = undefined,
		removeAddedPeril = undefined,
	} = {} as SaveOptions,
	timezone = 'America/Los_Angeles',
	currUpdateObj: EndorsementResponse | RenewalResponse,
	hiddenPerilLimits = false,
) => {
	const paths = getPathsForForms(fields, primaryFormName);
	let updateObject = {
		addExposures: [...currUpdateObj.addExposures],
		addFieldGroups: [...currUpdateObj.addFieldGroups],
		endExposures: [...currUpdateObj.endExposures],
		fieldValues: { ...currUpdateObj.fieldValues },
		removeFieldGroups: [...currUpdateObj.removeFieldGroups],
		updateExposures: [...currUpdateObj.updateExposures],
		updateFieldGroups: [...currUpdateObj.updateFieldGroups],
	} as EndorsementUpdateRequest | RenewalUpdateRequest;

	paths.forEach((key) => {
		let newUpdatedObject;
		const currDirtyFields = { [key]: dlv(dirtyFields, key, {}) };
		const currFormValues = { [key]: dlv(formValues, key, {}) };
		const currFields = {
			[key]: dlv(fields, key, {}),
		} as FormShape<FormBuilder>;

		switch (true) {
			// Top level form update
			case primaryFormName === key:
				{
					newUpdatedObject = updateRequestBuilder<
						EndorsementUpdateRequest | RenewalUpdateRequest
					>(
						currDirtyFields,
						currFormValues,
						currFields,
						key,
						timezone,
						false,
						true,
					);
					const mergedUpdate = mergeCurrentAndUpdateObjects(
						newUpdatedObject,
						currUpdateObj,
					);

					// Clean up helpers
					if (mergedUpdate.allAddFieldGroupsDeleted) {
						delete mergedUpdate.allAddFieldGroupsDeleted;
					}
					updateObject = { ...mergedUpdate };
				}
				break;
			// Added peril update on added exposure
			case key.split('.').length === 2 &&
				key.split('.')[0].includes('exposure') &&
				key.split('.')[1].includes('peril'):
				{
					const [exposureLocator, perilLocatorId] = key.split('.');
					const exposureLocatorIndex = parseInt(exposureLocator.split('-')[1]);
					const perilLocatorIndex = parseInt(perilLocatorId.split('-')[1]);

					const newPerilObject = createPerilRequestBuilder(
						currDirtyFields,
						currFormValues,
						currFields,
						key,
						timezone,
						false,
						true,
					);

					const currentPerilObject = dlv(
						currUpdateObj,
						`addExposures.${exposureLocatorIndex}.perils.${perilLocatorIndex}`,
						{},
					) as PerilCreateRequest;

					const mergedPerilCreateRequest = mergePerilCreateObjects(
						newPerilObject,
						currentPerilObject,
						currentPerilObject.locator,
					);

					// Clean up helpers
					if (mergedPerilCreateRequest.allAddFieldGroupsDeleted) {
						delete mergedPerilCreateRequest.allAddFieldGroupsDeleted;
					}

					updateObject.addExposures[exposureLocatorIndex].perils[
						perilLocatorIndex
					] = mergedPerilCreateRequest;
				}

				break;
			// Added peril update on existing exposure update
			case key.split('.').length === 2 &&
				!key.split('.')[0].includes('exposure') &&
				key.split('.')[1].includes('peril'):
				{
					const [exposureLocator, perilLocatorId] = key.split('.');
					const perilLocatorIndex = parseInt(perilLocatorId.split('-')[1]);
					const newPerilObject = createPerilRequestBuilder(
						currDirtyFields,
						currFormValues,
						currFields,
						key,
						timezone,
						false,
						true,
					);

					let updateExposureObjectIndex =
						currUpdateObj.updateExposures.findIndex((exposure) => {
							return exposure.exposureLocator === exposureLocator;
						});

					const updateExposureObject =
						currUpdateObj.updateExposures[updateExposureObjectIndex];

					const currentPerilObject = dlv(
						updateExposureObject,
						`addPerils.${perilLocatorIndex}`,
						{},
					) as PerilCreateRequest;

					const mergedPerilCreateRequest = mergePerilCreateObjects(
						newPerilObject,
						currentPerilObject,
						currentPerilObject.locator,
					);
					const perilIndexFromUpdateObject =
						updateExposureObject.addPerils.findIndex(
							(peril) => peril.locator === mergedPerilCreateRequest.locator,
						);

					// find correct exposure object index for updateObject since updateObject might hold different items than currUpdateObj.updateExposures
					updateExposureObjectIndex = updateObject.updateExposures.findIndex(
						(exposure) => {
							return exposure.exposureLocator === exposureLocator;
						},
					);

					// Clean up helpers
					if (mergedPerilCreateRequest.allAddFieldGroupsDeleted) {
						delete mergedPerilCreateRequest.allAddFieldGroupsDeleted;
					}

					updateObject.updateExposures[updateExposureObjectIndex].addPerils[
						perilIndexFromUpdateObject
					] = mergedPerilCreateRequest;
				}
				break;
			// Existing peril update on existing exposure update
			case key.split('.').length === 2 &&
				!key.split('.')[0].includes('exposure') &&
				!key.split('.')[1].includes('peril'):
				{
					const [exposureLocator, perilLocator] = key.split('.');
					const existingUpdateExposure = currUpdateObj.updateExposures.find(
						(exposure) => exposure.exposureLocator === exposureLocator,
					);

					const existingUpdatePeril =
						existingUpdateExposure &&
						existingUpdateExposure.updatePerils.find(
							(peril) => peril.perilLocator === perilLocator,
						);

					const newPerilUpdateObject = updatePerilRequestBuilder(
						currDirtyFields,
						currFormValues,
						currFields,
						key,
						timezone,
						true,
						hiddenPerilLimits,
					);

					const mergedUpdatePerilObject =
						mergeCurrentPerilUpdateObjectAndPerilUpdateObject(
							newPerilUpdateObject,
							existingUpdatePeril || ({} as PerilUpdateRequest),
							perilLocator,
						);

					// Remove any deleted field groups
					if (mergedUpdatePerilObject.removeFieldGroups) {
						mergedUpdatePerilObject.updateFieldGroups =
							mergedUpdatePerilObject.updateFieldGroups &&
							mergedUpdatePerilObject.updateFieldGroups.filter((fg) => {
								return !mergedUpdatePerilObject.removeFieldGroups.includes(
									fg.fieldGroupLocator,
								);
							});
					}

					// Clean up helpers
					if (mergedUpdatePerilObject.allAddFieldGroupsDeleted) {
						delete mergedUpdatePerilObject.allAddFieldGroupsDeleted;
					}

					if (Object.keys(mergedUpdatePerilObject).length > 1) {
						const existingUpdatedExposure = updateObject.updateExposures.find(
							(exposure) => exposure.exposureLocator === exposureLocator,
						);

						if (existingUpdatedExposure) {
							const existingUpdatePerilIndex =
								existingUpdatedExposure.updatePerils.findIndex(
									(peril) => peril.perilLocator === perilLocator,
								);

							if (existingUpdatePerilIndex > -1) {
								existingUpdatedExposure.updatePerils[existingUpdatePerilIndex] =
									mergedUpdatePerilObject;
							} else {
								existingUpdatedExposure.updatePerils.push(
									mergedUpdatePerilObject,
								);
							}
						} else {
							const newExposure = {
								exposureLocator,
								updatePerils: [mergedUpdatePerilObject],
								addPerils: [],
								fieldValues: {},
								addFieldGroups: [],
								updateFieldGroups: [],
								removeFieldGroups: [],
								endPerils: [],
							} as
								| EndorsementExposureUpdateRequest
								| RenewalExposureUpdateRequest;
							updateObject.updateExposures.push(newExposure);
						}
					}
				}

				break;
			// Added exposure update
			case key.split('.').length === 1 &&
				key.split('.')[0].includes('exposure'):
				{
					const exposureLocatorIndex = parseInt(key.split('-')[1]);
					const fieldsForExposureRequestBuilder = {
						...currFields,
						[key]: {
							...currFields[key],
							addFieldGroups: currFields[key].fieldGroups,
						},
					} as FormShape<FormBuilder>;

					const newUpdateObject = updateRequestBuilder<ExposureCreateRequest>(
						currDirtyFields,
						currFormValues,
						fieldsForExposureRequestBuilder,
						key,
						timezone,
						true,
						true,
					);

					const existingAddExposure = currUpdateObj.addExposures
						? currUpdateObj.addExposures[exposureLocatorIndex]
						: ({} as ExposureCreateRequest);

					const existingAddExposureIndex = currUpdateObj.addExposures.findIndex(
						(exposure) =>
							exposure.exposureLocator === existingAddExposure.exposureLocator,
					);

					const mergedAddObject =
						mergeCurrentExposureAddObjectAndExposureAddObject(
							newUpdateObject,
							existingAddExposure,
							existingAddExposure.exposureLocator,
						);

					// Clean up helpers
					if (mergedAddObject.allAddFieldGroupsDeleted) {
						delete mergedAddObject.allAddFieldGroupsDeleted;
					}

					if (existingAddExposureIndex > -1) {
						updateObject.addExposures[existingAddExposureIndex] =
							mergedAddObject;
					} else {
						updateObject.addExposures
							? updateObject.addExposures.push(mergedAddObject)
							: (updateObject.addExposures = [mergedAddObject]);
					}
				}
				break;
			// Existing exposure update
			case key.split('.').length === 1:
				{
					const existingUpdateExposure = currUpdateObj.updateExposures.find(
						(exposure) => exposure.exposureLocator === key,
					);

					const newUpdateObject = updateRequestBuilder<
						EndorsementExposureUpdateRequest | RenewalExposureUpdateRequest
					>(
						currDirtyFields,
						currFormValues,
						currFields,
						key,
						timezone,
						false,
						true,
					);

					const mergedUpdateObject =
						mergeCurrentExposureUpdateObjectAndExposureUpdateObject(
							newUpdateObject,
							existingUpdateExposure,
							key,
						);

					// Clean up helpers
					if (mergedUpdateObject.allAddFieldGroupsDeleted) {
						delete mergedUpdateObject.allAddFieldGroupsDeleted;
					}

					updateObject.updateExposures.push(mergedUpdateObject);
				}
				break;

			default:
				break;
		}
	});

	// Remove requests that have no updates in any of their required fields
	updateObject.updateExposures = updateObject.updateExposures.filter(
		(exposure) =>
			exposure.addPerils?.length > 0 ||
			exposure.updatePerils?.length > 0 ||
			exposure.endPerils?.length > 0 ||
			exposure.updateFieldGroups?.length > 0 ||
			exposure.addFieldGroups?.length > 0 ||
			exposure.removeFieldGroups?.length > 0 ||
			Object.keys(exposure.fieldValues)?.length > 0,
	);

	if (addExposure) {
		updateObject.addExposures.push(addExposure);
	}

	if (addPeril) {
		const { peril, exposureLocator, exposureIsAdded, exposureIndex } = addPeril;

		// Handle adding peril to added exposure
		if (exposureIsAdded) {
			if (exposureIndex > -1) {
				// push new peril to added exposure's peril array
				updateObject.addExposures[exposureIndex].perils.push(peril);
			} else {
				console.error('Attempting to add peril on non-existent exposure');
			}
		} else {
			// handle adding peril to existing exposure
			const existingExposureIndex = updateObject.updateExposures.findIndex(
				(exposure) => exposure.exposureLocator === exposureLocator,
			);
			if (existingExposureIndex > -1) {
				updateObject.updateExposures[existingExposureIndex].addPerils.push(
					peril,
				);
			} else {
				// existing exposure hasn't been updated yet, so add it to updateExposures[]
				updateObject.updateExposures && updateObject.updateExposures.length > 0
					? updateObject.updateExposures.push({
							exposureLocator: exposureLocator,
							addPerils: [peril],
							fieldValues: {},
							addFieldGroups: [],
							updateFieldGroups: [],
							removeFieldGroups: [],
							endPerils: [],
							updatePerils: [],
					  })
					: (updateObject.updateExposures = [
							{
								exposureLocator: exposureLocator,
								addPerils: [peril],
								fieldValues: {},
								addFieldGroups: [],
								updateFieldGroups: [],
								removeFieldGroups: [],
								endPerils: [],
								updatePerils: [],
							},
					  ]);
			}
		}
	}

	if (removeExposure) {
		// Add removed exposure to removeExposures[]
		updateObject.endExposures.push(removeExposure.exposureLocator);
		const existingExposureUpdates = updateObject?.updateExposures?.findIndex(
			(exposure) => exposure.exposureLocator === removeExposure.exposureLocator,
		);
		if (existingExposureUpdates > -1) {
			// Delete existing exposure updates if they exist
			updateObject.updateExposures.splice(existingExposureUpdates, 1);
		}
	}

	if (removePeril) {
		let existingExposureUpdates = updateObject?.updateExposures?.findIndex(
			(exposure) => exposure.exposureLocator === removePeril.exposureLocator,
		);
		if (existingExposureUpdates > -1) {
			// Add removed peril to updated exposure's endPeril[]
			updateObject.updateExposures[existingExposureUpdates].endPerils.push(
				removePeril.perilLocator,
			);
			// Check if there are any existing peril updates we need to remove
			const existingPerilUpdates = updateObject?.updateExposures[
				existingExposureUpdates
			]?.updatePerils?.findIndex(
				(peril) => peril.perilLocator === removePeril.perilLocator,
			);
			if (existingPerilUpdates > -1) {
				// Delete existing peril updates if they exist
				updateObject.updateExposures[
					existingExposureUpdates
				].updatePerils.splice(existingPerilUpdates, 1);
			}
		} else {
			// Removing a peril in an existing exposure that hasn't been updated yet--add it to updateExposures array
			updateObject.updateExposures.push({
				exposureLocator: removePeril.exposureLocator,
				endPerils: [removePeril.perilLocator],
				fieldValues: {},
				addFieldGroups: [],
				updateFieldGroups: [],
				removeFieldGroups: [],
				addPerils: [],
				updatePerils: [],
			});
		}
	}

	if (removeAddedExposure) {
		// Filter out added exposure from addExposures[]
		updateObject.addExposures = updateObject?.addExposures?.filter(
			(exposure) =>
				exposure.exposureLocator !== removeAddedExposure.exposureLocator,
		);
	}

	if (removeAddedPeril) {
		const { exposureLocator, perilLocator, exposureIsAdded } = removeAddedPeril;

		if (exposureIsAdded) {
			const exposureIndex = removeAddedPeril.exposureIndex
				? removeAddedPeril.exposureIndex
				: updateObject.addExposures.findIndex(
						(exposure: ExposureCreateRequest) => {
							return exposure.exposureLocator === exposureLocator;
						},
				  );
			if (exposureIndex > -1) {
				// Filter out added peril from added exposure's peril array
				updateObject.addExposures[exposureIndex].perils =
					updateObject.addExposures[exposureIndex].perils.filter(
						(peril) => peril.locator !== perilLocator,
					);
			} else {
				console.error('Attempting to remove peril on non-existent exposure');
			}
		} else {
			const exposureIndex = removeAddedPeril.exposureIndex
				? removeAddedPeril.exposureIndex
				: updateObject.updateExposures.findIndex(
						(exposure: ExposureUpdateRequest) => {
							return exposure.exposureLocator === exposureLocator;
						},
				  );
			if (exposureIndex > -1) {
				// Filter out added peril from existing exposure's peril array
				updateObject.updateExposures[exposureIndex].addPerils =
					updateObject.updateExposures[exposureIndex].addPerils.filter(
						(peril) => peril.locator !== perilLocator,
					);
			} else {
				// Removing a peril in an existing exposure that hasn't been updated yet--set addPerils to empty array
				// Technically, this shouldn't be possible, as the exposure should be in updateExposures[] if a peril was added
				updateObject.updateExposures.push({
					exposureLocator: exposureLocator,
					addPerils: [],
					fieldValues: {},
					addFieldGroups: [],
					updateFieldGroups: [],
					removeFieldGroups: [],
					endPerils: [],
					updatePerils: [],
				});
			}
		}
	}

	// filter out all ended/removed objects from all updated object arrays (exposures, perils, field groups)
	updateObject = filterRemovedFromUpdated(updateObject);

	return updateObject;
};

export default endorsementRenewalUpdateBuilder;
